/**
 * Root route shell — chrome + outlet + auth guard (plan §6.8.11).
 * Feature routes attach as children; guard is composition-only here.
 * Staleness banner evaluates §6.7 triggers over `/v1/meta` (FR-A8 · Phase 9c).
 */
import { useQueryClient } from '@tanstack/react-query';
import { Outlet, createRootRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import { useCallback, useEffect, type JSX } from 'react';

import type { BookFilterParam } from '@meridian/contracts';

import { AccountMenu } from '@/auth/account-menu';
import { useAuth } from '@/auth/auth-context';
import { RouteGuard } from '@/auth/route-guard';
import type { LoginRedirectTarget } from '@/auth/route-guard';
import { AppFooter } from '@/components/chrome/app-footer';
import { AppHeader } from '@/components/chrome/app-header';
import { BookWarningsBanner } from '@/components/chrome/book-warnings-banner';
import { StaleDataBanner } from '@/components/chrome/stale-data-banner';
import { SyntheticDataBanner } from '@/components/chrome/synthetic-data-banner';
import { EmptyState } from '@/components/primitives/empty-state';
import { loadConfig } from '@/config';
import type { AppConfig } from '@/config';
import { useBookQuery } from '@/data/queries/dashboard';
import { useMetaQuery } from '@/data/queries/meta';
import { dashboardKeys } from '@/data/query-keys/dashboard';
import { metaPhaseOf } from '@/lib/meta-phase';
import { useShellStaleness } from '@/lib/use-shell-staleness';
import { useBookParam } from '@/state/use-book-param';
import { useIndexParam } from '@/state/use-index-param';
import { useWindowParam } from '@/state/use-window-param';

const config = loadConfig();

function GuardedOutlet({
  appConfig,
  pathname,
  search,
  onRedirectToLogin,
  isLoginRoute,
}: {
  readonly appConfig: AppConfig;
  readonly pathname: string;
  readonly search: Record<string, unknown>;
  readonly onRedirectToLogin: (target: LoginRedirectTarget) => void;
  readonly isLoginRoute: boolean;
}): JSX.Element {
  if (isLoginRoute) return <Outlet />;
  return (
    <RouteGuard
      config={appConfig}
      returnTo={{ pathname, search }}
      onRedirectToLogin={onRedirectToLogin}
    >
      <Outlet />
    </RouteGuard>
  );
}

interface ShellNavigation {
  readonly pathname: string;
  readonly search: Record<string, unknown>;
  readonly isLoginRoute: boolean;
  readonly onRedirectToLogin: (target: LoginRedirectTarget) => void;
  readonly onSignedOut: () => Promise<void>;
}

/**
 * The shell's whole relationship with the router, in one place.
 *
 * Extracted when the account slot landed: `AppShell` was already at the 50-line
 * ceiling (style §3), and the alternative — trimming the markup — would have
 * moved composition out of the one component whose job is composition.
 */
function useShellNavigation(): ShellNavigation {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const search = useRouterState({
    select: (s) => s.location.search as Record<string, unknown>,
  });
  const onRedirectToLogin = useCallback(
    (target: LoginRedirectTarget) => {
      void navigate({
        to: '/login',
        state: { returnTo: target.returnTo },
      } as never);
    },
    [navigate],
  );
  /*
   * Sign-out navigates WITHOUT a return-to, deliberately.
   *
   * `AccountMenu` AWAITS this before `logout()` flips status, so `/login` is
   * already the location (and RouteGuard is unmounted) when the session dies.
   * A later guard redirect would stash the page just left as `returnTo`.
   *
   * The promise is returned rather than voided precisely so the menu can await
   * it: an unawaited navigation only starts the transition, leaving the
   * ordering to win a race it does not always win.
   */
  const onSignedOut = useCallback(async () => {
    await navigate({ to: '/login' } as never);
  }, [navigate]);
  return {
    pathname,
    search,
    isLoginRoute: pathname === '/login' || pathname.startsWith('/login/'),
    onRedirectToLogin,
    onSignedOut,
  };
}

function useAccountSlot(
  isLoginRoute: boolean,
  onSignedOut: () => Promise<void>,
): JSX.Element | undefined {
  const { status } = useAuth();
  if (isLoginRoute || status !== 'authenticated') return undefined;
  return <AccountMenu config={config} onSignOut={onSignedOut} />;
}

/**
 * The manual book as the shell needs it (ADR-0042): the URL flag, plus the
 * payload facts two chrome components bind to. Extracted so `AppShell` states
 * what it renders rather than unwrapping the same query twice — the toggle
 * needs a lot count and the banner needs warnings, and neither should carry a
 * `?.` chain into JSX.
 */
function useBookShell(): {
  readonly value: BookFilterParam | undefined;
  readonly setValue: (next: BookFilterParam | undefined) => void;
  readonly lotCount: number;
  readonly warnings: readonly string[];
} {
  const { status } = useAuth();
  const queryClient = useQueryClient();
  const { book, setBook } = useBookParam();
  const live = status === 'authenticated' || status === 'bypassed';
  const query = useBookQuery(live);
  useEffect(() => {
    if (status !== 'unauthenticated') return;
    queryClient.removeQueries({ queryKey: dashboardKeys.book() });
  }, [status, queryClient]);
  return {
    value: book,
    setValue: setBook,
    /* Zero until the book resolves, which disables the toggle rather than
       offering a filter over a book nobody has loaded yet. Unauthenticated
       chrome must not render a cached lot count from the previous session. */
    lotCount: live ? (query.data?.lots.length ?? 0) : 0,
    warnings: live ? (query.data?.warnings ?? []) : [],
  };
}

function AppShell(): JSX.Element {
  const { data: meta, isError } = useMetaQuery();
  const { index, setIndex } = useIndexParam();
  const book = useBookShell();
  const { window, setWindow } = useWindowParam();
  const { pathname, search, isLoginRoute, onRedirectToLogin, onSignedOut } = useShellNavigation();
  const account = useAccountSlot(isLoginRoute, onSignedOut);
  const staleness = useShellStaleness(meta);
  const metaPhase = metaPhaseOf(isError, meta !== undefined);

  return (
    <div className="mt-page" {...(staleness.stale ? { 'data-stale': 'true' as const } : {})}>
      {meta !== undefined ? <SyntheticDataBanner dataSource={meta.dataSource} /> : null}
      <StaleDataBanner stale={staleness.stale} reasons={staleness.reasons} />
      {/* Beneath the freshness banner: a partly-unreadable book is the
          operator's own file, and a stale market is the more urgent signal
          when both fire. */}
      <BookWarningsBanner warnings={book.warnings} />
      {/*
       * The account slot is what makes R3 — "add a passkey from the product UI"
       * — reachable at all; `AccountMenu` is otherwise composed nowhere.
       *
       * Omitted on `/login` (an authenticated visitor must not meet a sign-out
       * control on the sign-in page) and while unauthenticated, so the header
       * does not mount an empty trailing wrapper. AccountMenu also self-gates.
       */}
      <AppHeader
        index={index}
        window={window}
        onIndexChange={setIndex}
        onWindowChange={setWindow}
        book={book.value}
        onBookChange={book.setValue}
        bookLotCount={book.lotCount}
        {...(account === undefined ? {} : { account })}
      />
      <main className="mt-main">
        <GuardedOutlet
          appConfig={config}
          pathname={pathname}
          search={search}
          onRedirectToLogin={onRedirectToLogin}
          isLoginRoute={isLoginRoute}
        />
      </main>
      <AppFooter
        universeSize={meta?.universeSize ?? null}
        asOf={meta?.asOf ?? null}
        stale={staleness.stale}
        metaPhase={metaPhase}
      />
    </div>
  );
}

function RootNotFound(): JSX.Element {
  return (
    <EmptyState
      title="Screen not found"
      description={
        <span>
          This screen is not available. <a href="/">Back to dashboard</a>
        </span>
      }
    />
  );
}

export const rootRoute = createRootRoute({
  component: AppShell,
  notFoundComponent: RootNotFound,
});
